import { CreateCompanyPanelView } from '../view/CreateCompanyPanelView';
import { templateCreateCompanyPanel } from '../view/template';
import { BaseWebComponentController, WebComponentEvent } from '../../../../framework/BaseWebComponentController';
import { CreateCompanyPanelWebComponent } from '../component/CreateCompanyPanelWebComponent';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastPositionX, ToastPositionY, ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { CreateCompanyStep } from '../types/CreateCompanyStep';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { combineLatest, Subscription } from 'rxjs';
import _ from 'lodash';
import { CreateCompanyRecordResponse } from '../types/CreateCompanyRecordResponse';
import { CreateCompanyEvent } from '../types/CreateCompanyEvent';
import { CompanyDetailsEvents } from '../../../common/companyDetailsForm/types/CompanyDetailsEvents';
import { CreateCompanyViewData } from '../types/CreateCompanyViewData';
import { OnCompanyPanelClose } from '../../../common/events/CompanyPanelEvent';
import { LicenseMonthlySubscriptionSubFormEvents } from '../../../../license/licenseMonthlySubscriptionSubForm/types/LicenseMonthlySubscriptionSubFormEvents';

export class CreateCompanyPanelController extends BaseWebComponentController {
  public component: CreateCompanyPanelWebComponent;
  public view: CreateCompanyPanelView;
  public viewConstructor: typeof CreateCompanyPanelView;

  private createStep: CreateCompanyStep;
  private validSubscription: Subscription | null;

  constructor(component: CreateCompanyPanelWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: CreateCompanyPanelView,
      viewTemplate: templateCreateCompanyPanel,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$wrapper,
        event: CreateCompanyEvent.OnViewInit,
        callback: this.showCompanyDetailsForm,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.showCancelWarningDialog,
      },
      {
        element: this.view.dom.$backButton,
        event: 'click',
        callback: this.backHandler,
      },
      {
        element: this.view.dom.$nextButton,
        event: 'click',
        callback: this.nextHandler,
      },
      {
        element: this.view.dom.$companyDetailsForm,
        event: CompanyDetailsEvents.ManageNetworkChange,
        callback: this.handleCanManageNetworksChange,
      },
      {
        element: this.view.dom.$companyDetailsForm,
        event: CompanyDetailsEvents.CompanyTypeChange,
        callback: this.handleCompanyTypeChange,
      },
      {
        element: this.view.dom.$companyDetailsForm,
        event: CompanyDetailsEvents.ManageByAboveChange,
        callback: this.handleCanManageByAboveChange,
      },
      {
        element: this.view.dom.$companyLicenseMainForm,
        event: LicenseMonthlySubscriptionSubFormEvents.CanBeManagedByAbove,
        callback: this.handleCanManageByAboveChangeFromLicensingForm,
      },
    ];
  }

  public async getDataForView(): Promise<CreateCompanyViewData> {
    return {
      isCustomFieldsAvailable:
        await this.dependencies.api.clients.CustomFields.ShouldCustomFieldsBeVisible().toPromise(),
    };
  }

  private handleCanManageNetworksChange(event: WebComponentEvent) {
    if (!event.detail.canManageNetworks) {
      this.view.dom.$companyLicenseMainForm.controller.resetLicensingForm();
    }
  }

  private handleCompanyTypeChange(event: WebComponentEvent) {
    if (!event.detail.companyType) {
      this.view.dom.$companyLicenseMainForm.controller.resetLicensingForm(true);
    }
  }

  private handleCanManageByAboveChange(event: WebComponentEvent) {
    this.view.dom.$companyLicenseMainForm.setCanBeManagedByAbove(event.detail.canBeManagedByAbove);
  }

  private handleCanManageByAboveChangeFromLicensingForm() {
    this.view.dom.$companyDetailsForm.setCanBeManagedByAbove(true);
  }

  private async backHandler(event: Event) {
    switch (this.createStep) {
      case CreateCompanyStep.Authentication:
        await this.showCompanyDetailsForm(event);
        break;
      case CreateCompanyStep.AddLicense:
        await this.showAuthenticationForm(event);
        break;
    }
  }

  private async nextHandler(event: Event) {
    switch (this.createStep) {
      case CreateCompanyStep.CompanyDetails:
        this.showAuthenticationForm(event);
        break;
      case CreateCompanyStep.Authentication:
        await this.showAddLicenseForm(event);
        break;
      case CreateCompanyStep.AddLicense:
        await this.createCompany();
        break;
    }
  }

  private showCompanyDetailsForm(event: Event | null) {
    this.switchCreateStep(CreateCompanyStep.CompanyDetails);

    // enable / disable "Next" button based on formGroup's validity
    this.validSubscription?.unsubscribe();
    this.validSubscription = this.view.dom.$companyDetailsForm.valid$.subscribe({
      next: valid => {
        this.view.dom.$nextButton.disabled = !valid;
      },
    });

    this.view.dom.$wizard.updateStep(event);
    this.view.dom.$nextButton.setAttribute('data-step', CreateCompanyStep.Authentication.toString());

    toggleVisibility(this.view.dom.$backButton, false);
    this.view.dom.$nextButton.innerText = this.dependencies.translateFn('Next');
  }

  private showAuthenticationForm(event: Event) {
    this.switchCreateStep(CreateCompanyStep.Authentication);

    // enable / disable "Next" button based on formGroup's validity
    this.validSubscription?.unsubscribe();
    this.validSubscription = this.view.dom.$companyAuthenticationForm.getFormGroup().valid$.subscribe({
      next: valid => {
        this.view.dom.$nextButton.disabled = !valid;
      },
    });

    this.view.dom.$wizard.updateStep(event);
    this.view.dom.$backButton.setAttribute('data-step', CreateCompanyStep.CompanyDetails.toString());
    this.view.dom.$nextButton.setAttribute('data-step', CreateCompanyStep.AddLicense.toString());

    toggleVisibility(this.view.dom.$backButton, true);
    this.view.dom.$nextButton.innerText = this.dependencies.translateFn('Next');
  }

  private async showAddLicenseForm(event: Event) {
    this.switchCreateStep(CreateCompanyStep.AddLicense);

    const canManageNetworks = this.view.dom.$companyDetailsForm.getCanManageNetworks();
    this.view.dom.$companyLicenseMainForm.setCanManageNetworks(canManageNetworks);

    const companyType = this.view.dom.$companyDetailsForm.getCompanyType();
    this.view.dom.$companyLicenseMainForm.setCompanyType(companyType);

    // check that the prepaid subform has issues, if so, disable the "Save/Next" button
    const prepaidSubformHasIssues = this.view.dom.$companyLicenseMainForm.prepaidSubformHasIssues;

    // enable / disable "Next" button based on formGroup's validity
    this.validSubscription?.unsubscribe();

    if (this.view.dom.$customFieldsForm.isCustomFieldsFormAvailable) {
      // enable / disable "Next" button based on license main form and custom fields
      this.validSubscription = combineLatest([
        this.view.dom.$companyLicenseMainForm.valid$,
        this.view.dom.$customFieldsForm.valid$,
      ]).subscribe({
        next: ([companyLicenseMainFormValidity, customFieldsFormValidity]) => {
          this.view.dom.$nextButton.disabled =
            !(companyLicenseMainFormValidity && customFieldsFormValidity) || prepaidSubformHasIssues;
        },
      });
    } else {
      // enable / disable "Next" button based on license main form
      this.validSubscription = this.view.dom.$companyLicenseMainForm.valid$.subscribe({
        next: companyLicenseMainFormValidity => {
          this.view.dom.$nextButton.disabled = !companyLicenseMainFormValidity || prepaidSubformHasIssues;
        },
      });
    }

    this.view.dom.$wizard.updateStep(event);
    this.view.dom.$backButton.setAttribute('data-step', CreateCompanyStep.Authentication.toString());

    toggleVisibility(this.view.dom.$backButton, true);
    this.view.dom.$nextButton.innerText = this.dependencies.translateFn('Save and Add Company');
  }

  private switchCreateStep(step: CreateCompanyStep) {
    this.createStep = step;

    toggleVisibility(this.view.dom.$companyDetailsFormWrapper, step === CreateCompanyStep.CompanyDetails);
    toggleVisibility(this.view.dom.$companyAuthenticationFormWrapper, step === CreateCompanyStep.Authentication);
    toggleVisibility(this.view.dom.$companyLicenseMainFormWrapper, step === CreateCompanyStep.AddLicense);
    toggleVisibility(
      this.view.dom.$customFieldsForm,
      step === CreateCompanyStep.AddLicense && this.view.dom.$customFieldsForm.isCustomFieldsFormAvailable
    );
  }

  private async createCompany(): Promise<void> {
    // show spinner
    this.view.dom.$nextButton.showSpinner = true;
    this.view.dom.$nextButton.disabled = true;

    try {
      await this.view.dom.$companyDetailsForm.uploadLogo();
      const createdCompanyId = await this.createCompanyRecord();

      if (this.view.dom.$customFieldsForm.isCustomFieldsFormAvailable && createdCompanyId) {
        await this.createCustomFieldsRecord(createdCompanyId);
      }

      this.showSuccessToast();

      // remove the spinner
      this.view.dom.$nextButton.showSpinner = false;
      this.view.dom.$nextButton.disabled = false;

      this.emitCloseCompanyWindowEvent();
    } catch (error) {
      // if the exception is AuthenticationSecurityException it is already treated by the general session exception
      if (error?.exceptionType === 'Exceptions\\AuthenticationSecurityException') {
        return;
      }

      this.showErrorToast(error);

      // remove the spinner
      this.view.dom.$nextButton.showSpinner = false;
      this.view.dom.$nextButton.disabled = false;
    }
  }

  private async createCompanyRecord(): Promise<string | null> {
    const companyRecord = _.merge(
      this.view.dom.$companyDetailsForm.value,
      this.view.dom.$companyAuthenticationForm.getFormGroup().value,
      this.view.dom.$companyLicenseMainForm.value
    );

    return CreateCompanyRecordResponse.getCompanyRecordId(
      await this.dependencies.api.clients.Company.CreateRecord(companyRecord).toPromise()
    );
  }

  private async createCustomFieldsRecord(createdCompanyId: string): Promise<void> {
    const value = this.view.dom.$customFieldsForm.value;

    if (!value.fields || !Array.isArray(value.fields) || !value.fields.length) {
      return;
    }

    _.set(value, ['managedCompanyId'], createdCompanyId);
    await this.dependencies.api.clients.CustomFields.CreateCustomFieldsRecord(value).toPromise();
  }

  private emitCloseCompanyWindowEvent() {
    this.component.dispatchEvent(new OnCompanyPanelClose());
  }

  private showCancelWarningDialog() {
    const windowBodyHtml = `
      <gz-text fontSize="14">
        ${this.dependencies.translateFn('Changes you have made will be lost.')}
      </gz-text>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${this.dependencies.translateFn('Discard')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Keep changes')}
      </gz-button>`;

    const windowTitle = this.dependencies.translateFn('Discard changes?');
    const confirmCallback = () => {
      this.emitCloseCompanyWindowEvent();
    };

    GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, windowTitle, null, confirmCallback);
  }

  private showSuccessToast() {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.dependencies.translateFn('Company successfully created.'),
      severity: ToastSeverity.SUCCESS,
    });
  }

  private showErrorToast(error: Error) {
    let errorMessage;

    if (error['exceptionType'] === 'Exceptions\\NoInternetConnection') {
      errorMessage = this.dependencies.translateFn(
        'The licensing server is temporarily unavailable. Please try again later.'
      );
    } else {
      errorMessage = error?.message || this.translator.getGenericErrorMessage();
    }

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: ToastSeverity.ERROR,
    });
  }
}
